
import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HeartPulse, Brain, ActivitySquare, Sparkles } from 'lucide-react';
import ArtistHealthMetric from '../ArtistHealthMetric';
import MentalWellnessInsight from '../wellness/MentalWellnessInsights';
import WellnessActivities, { WellnessActivity } from '../wellness/WellnessActivities';
import { mentalWellnessData } from '../data/mentalWellnessData';

interface MentalWellnessTabContentProps {
  onResetModalOpen: () => void;
}

const MentalWellnessTabContent: React.FC<MentalWellnessTabContentProps> = ({ 
  onResetModalOpen 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="col-span-1 md:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl mb-1 flex items-center gap-2">
                <HeartPulse className="h-5 w-5 text-rose-500" />
                Mental Wellness Check
              </CardTitle>
              <CardDescription>
                AI-powered wellness analysis and recommendations
              </CardDescription>
            </div>
            <Button 
              onClick={onResetModalOpen}
              size="sm"
              className="mental-reset-button"
            >
              <Sparkles className="h-4 w-4" />
              Creative Reset
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <ArtistHealthMetric 
              title="Stress Level" 
              value={mentalWellnessData.scores.stress} 
              icon={<Brain className="h-4 w-4 text-amber-500" />}
              change={15}
              changeLabel="↑ 15% this month"
              colorClass="bg-amber-500/10 text-amber-500 border-amber-500/30"
            />
            
            <ArtistHealthMetric 
              title="Burnout Risk" 
              value={mentalWellnessData.scores.burnoutRisk} 
              icon={<ActivitySquare className="h-4 w-4 text-rose-500" />}
              change={10}
              changeLabel="↑ 10% this month"
              colorClass="bg-rose-500/10 text-rose-500 border-rose-500/30" 
            />
            
            <ArtistHealthMetric 
              title="Creative Energy" 
              value={mentalWellnessData.scores.creativity} 
              icon={<Sparkles className="h-4 w-4 text-emerald-500" />}
              change={-5}
              changeLabel="↓ 5% this month"
              colorClass="bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
            />
            
            <ArtistHealthMetric 
              title="App Engagement" 
              value={mentalWellnessData.scores.workLifeBalance} 
              icon={<HeartPulse className="h-4 w-4 text-cyan-500" />}
              change={-12}
              changeLabel="↓ 12% this month"
              colorClass="bg-cyan-500/10 text-cyan-500 border-cyan-500/30"
            />
          </div>
          
          {mentalWellnessData.insights.map((insight, index) => (
            <MentalWellnessInsight key={index} insight={insight} />
          ))}
          
          <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-md">
            <div className="flex items-start gap-2">
              <Sparkles className="h-5 w-5 text-indigo-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">AI Wellness Tip:</span> Try the Pomodoro Technique to balance focused work with regular breaks. Work for 25 minutes, then take a 5-minute break to stretch or rest your eyes. After 4 cycles, take a longer 15-30 minute break.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <WellnessActivities 
        recentActivities={mentalWellnessData.recentActivities}
        onResetModalOpen={onResetModalOpen}
      />
    </div>
  );
};

export default MentalWellnessTabContent;
